/**
 * The MOS logo — red disc, white mountain, wordmark.
 *
 * The mark here is the same drawing as the browser tab, the phone home screen
 * and MOS Drive's tray icon (scripts/make-favicons.mjs,
 * desktop/mos-drive/Branding.cs): one mark, everywhere it appears.
 *
 * It descends from `src/imports/MOS_logo.svg` — the mountain is that file's
 * geometry (a triangle as wide as it is tall, on a plinth an eighth of its
 * height) and the disc occupies exactly the footprint its ring did, r=45 plus
 * a stroke of 2. Two departures, both deliberate:
 *
 *   · The ring is filled rather than outlined. A hairline ring can't survive a
 *     16px tab, and drawn thick enough to survive it reads as a bullseye. The
 *     disc gives the same circular silhouette at any size, and gives the
 *     charcoal mark something to sit on — on a dark tab strip or taskbar an
 *     unfilled charcoal logo is not faint, it is invisible.
 *   · The mountain is re-centred. Inside the ring it sat deliberately low,
 *     which reads as a mistake once there's no ring for it to sit in.
 *
 * The wordmark is the one element that takes a colour: charcoal on the
 * sign-in screens, white in the navbar.
 */

/** Marcstone red — `--marcstone-red` / `--accent` in globals.css. */
export const MOS_RED = '#F0533E';
/** Brand charcoal — the wordmark's default, for light grounds. */
export const MOS_INK = '#2C2A29';

const DISC_R = 46;

/**
 * Mountain half-width as a fraction of the disc radius.
 *
 * Mirrored in scripts/make-favicons.mjs (half-width 27 at r=50) so the logo
 * and the app icon are the same drawing at different scales. Picked by
 * rendering 0.50/0.54/0.56/0.60 at 16, 32, 64 and 112px: the plinth's bottom
 * corners reach the disc edge fastest, and past this the bottom margin
 * pinches while the top still has room.
 */
const MOUNTAIN_RATIO = 0.54;

/** The mountain at half-width `a`, centred in the 100-unit box. */
function mountainPoints(a: number): string {
  const triangle = 2 * a;            // as wide as it is tall
  const plinth = triangle / 8;
  const top = 50 - (triangle + plinth) / 2;
  const base = top + triangle;
  const bottom = base + plinth;
  const r = (n: number) => +n.toFixed(2);
  return [
    `${r(50 - a)},${r(base)}`,
    `50,${r(top)}`,
    `${r(50 + a)},${r(base)}`,
    `${r(50 + a)},${r(bottom)}`,
    `${r(50 - a)},${r(bottom)}`,
  ].join(' ');
}

const MOUNTAIN = mountainPoints(DISC_R * MOUNTAIN_RATIO);

/** The mark itself. Shared by both exports so they can't drift. */
function MarkShapes() {
  return (
    <>
      <circle cx={50} cy={50} r={DISC_R} fill={MOS_RED} />
      <polygon points={MOUNTAIN} fill="#ffffff" />
    </>
  );
}

interface MarkProps {
  /** Rendered height in px. The mark is square; the lockup is 1.98× as wide. */
  height?: number;
  /** Wordmark colour. Charcoal by default; white on dark grounds. */
  color?: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  title?: string;
}

/** Disc + mountain, no wordmark. Square. */
export function MosMark({ height = 64, className, style, onClick, title }: MarkProps) {
  return (
    <svg
      width={height}
      height={height}
      viewBox="0 0 100 100"
      role="img"
      aria-label="MOS"
      className={className}
      style={style}
      onClick={onClick}
    >
      {title ? <title>{title}</title> : null}
      <MarkShapes />
    </svg>
  );
}

/**
 * The full lockup: mark + "MOS".
 *
 * viewBox is cropped to the ink — x from 4 (the disc's left edge) to 202 (the
 * wordmark's right edge at 110 + 91.1) — so centring the element centres the
 * logo. The source file's own box runs to 300, a third of it empty; harmless
 * left-aligned, visibly off-centre in a centred hero.
 *
 * The wordmark is inline `<text>` rather than baked into an image so it can
 * use the page's Inter. (Inter and the OS sans both set "MOS" at 91.1px
 * bold-40, so this moves nothing — it stops the wordmark depending on which
 * machine is rendering it.)
 */
export function MosLockup({
  height = 60,
  color = MOS_INK,
  className,
  style,
  onClick,
  title,
}: MarkProps) {
  return (
    <svg
      width={height * 1.98}
      height={height}
      viewBox="4 0 198 100"
      role="img"
      aria-label="MOS"
      className={className}
      style={style}
      onClick={onClick}
    >
      {title ? <title>{title}</title> : null}
      <MarkShapes />
      <text
        x={110}
        y={60}
        fill={color}
        fontSize={40}
        fontWeight={700}
        fontFamily="Inter, Helvetica, sans-serif"
      >
        MOS
      </text>
    </svg>
  );
}
